/**
 * Pure game maths for the Heap Leach Slots easter egg — symbols, paylines, and
 * payout evaluation. Kept free of React so the scoring rules can be tested
 * directly; SlotMachineModal.js owns all the state and animation.
 */

export const SYMBOLS = [
  { id: 'gold',  emoji: '🥇', label: 'Gold Bar',       weight: 1 },
  { id: 'heap',  emoji: '🏔️', label: 'Heap Pad',       weight: 2 },
  { id: 'pick',  emoji: '⛏️', label: 'Pickaxe',        weight: 3 },
  { id: 'cash',  emoji: '💰', label: 'Pay Dirt',       weight: 3 },
  { id: 'flask', emoji: '⚗️', label: 'Reagent',        weight: 4 },
  { id: 'drop',  emoji: '💧', label: 'Leach Solution', weight: 5 },
  { id: 'rock',  emoji: '🪨', label: 'Ore',            weight: 6 },
];

/** Weighted pool: rarer symbols appear proportionally less often. */
const SYMBOL_POOL = SYMBOLS.flatMap(s => Array(s.weight).fill(s));

export const PAYOUT_TABLE = [
  { symbolId: 'gold',  triple: 500, pair: 30, label: 'JACKPOT!!' },
  { symbolId: 'heap',  triple: 200, pair: 20, label: 'BIG WIN!'  },
  { symbolId: 'pick',  triple: 100, pair: 15, label: 'WINNER!'   },
  { symbolId: 'cash',  triple: 80,  pair: 12, label: 'WINNER!'   },
  { symbolId: 'flask', triple: 60,  pair: 10, label: 'WIN!'      },
  { symbolId: 'drop',  triple: 40,  pair: 8,  label: 'WIN!'      },
  { symbolId: 'rock',  triple: 30,  pair: 5,  label: 'WIN!'      },
];

/** Fallback for a pair of a symbol missing from the payout table. */
const DEFAULT_PAIR_PAYOUT = 5;

export const GRID_COLS = 3;
export const GRID_ROWS = 3;

/**
 * Paylines across the 3×3 grid. `rows[col]` is the row this line reads from
 * that column, so a flat line repeats one row and a diagonal walks across.
 */
export const PAYLINES = [
  { id: 'middle',   label: 'Middle', rows: [1, 1, 1] },
  { id: 'top',      label: 'Top',    rows: [0, 0, 0] },
  { id: 'bottom',   label: 'Bottom', rows: [2, 2, 2] },
  { id: 'diagDown', label: 'Diag ↘', rows: [0, 1, 2] },
  { id: 'diagUp',   label: 'Diag ↗', rows: [2, 1, 0] },
];

/** PAYOUT_TABLE figures are quoted at this bet; other bets scale from it. */
export const BASE_BET = 10;
export const MIN_BET = 5;
export const MAX_BET = 50;
export const BET_STEP = 5;
export const STARTING_CREDITS = 100;

export const randomSymbol = () =>
  SYMBOL_POOL[Math.floor(Math.random() * SYMBOL_POOL.length)];

/** One reel: a column of GRID_ROWS symbols. */
export const randomColumn = () => Array.from({ length: GRID_ROWS }, randomSymbol);
export const randomGrid = () => Array.from({ length: GRID_COLS }, randomColumn);

/**
 * Easter egg inside the easter egg: staking exactly 35 on the Top line alone
 * rigs the spin — the top row is guaranteed to land three of a kind. Which
 * symbol lands is still weighted-random, so the payout varies.
 */
export const LUCKY_BET = 35;
export const LUCKY_LINE_ID = 'top';

export const isLuckyWager = (betPerLine, lineIds) =>
  betPerLine === LUCKY_BET &&
  lineIds.length === 1 &&
  lineIds[0] === LUCKY_LINE_ID;

/** A random grid whose top row is forced to a single (weighted-random) symbol. */
export function luckyGrid() {
  const grid = randomGrid();
  const winner = randomSymbol();
  for (const column of grid) column[0] = winner;
  return grid;
}

export const cellKey = (col, row) => `${col}-${row}`;

/**
 * Scores one payline against a settled grid.
 *
 * @param line   a PAYLINES entry
 * @param grid   grid[col][row] of symbol objects
 * @param multiplier  stake / BASE_BET
 * @returns {{win: number, label: string, cells: string[]}|null} null when the line misses
 */
export function scoreLine(line, grid, multiplier) {
  const ids = line.rows.map((row, col) => grid[col][row].id);
  const cellsFor = cols => cols.map(col => cellKey(col, line.rows[col]));

  if (ids[0] === ids[1] && ids[1] === ids[2]) {
    const payout = PAYOUT_TABLE.find(p => p.symbolId === ids[0]);
    if (!payout) return null;
    return {
      win: Math.round(payout.triple * multiplier),
      label: payout.label,
      cells: cellsFor([0, 1, 2]),
    };
  }

  const pairId = ids.find((id, i) => ids.indexOf(id) !== i);
  if (pairId) {
    const payout = PAYOUT_TABLE.find(p => p.symbolId === pairId);
    const matching = ids
      .map((id, col) => (id === pairId ? col : -1))
      .filter(col => col >= 0);
    return {
      win: Math.round((payout ? payout.pair : DEFAULT_PAIR_PAYOUT) * multiplier),
      label: 'Pair',
      cells: cellsFor(matching),
    };
  }

  return null;
}

/**
 * Scores every active payline. Lines pay independently and stack, so one spin
 * can hit several at once.
 *
 * @returns {{total: number, hits: Array, cells: string[], best: object|null}}
 */
export function evaluateSpin(grid, lineIds, stake) {
  const multiplier = stake / BASE_BET;
  const hits = [];

  for (const line of PAYLINES) {
    if (!lineIds.includes(line.id)) continue;
    const result = scoreLine(line, grid, multiplier);
    if (result) hits.push({ ...result, lineId: line.id, lineLabel: line.label });
  }

  const total = hits.reduce((sum, h) => sum + h.win, 0);
  const best = hits.length
    ? hits.reduce((a, b) => (b.win > a.win ? b : a))
    : null;

  return {
    total,
    hits,
    best,
    cells: [...new Set(hits.flatMap(h => h.cells))],
  };
}
